import {v4 as uuidv4} from "uuid";
import EventBus from "../../util/EventBus";
import EnterpriseComponentAttribute from "./EnterpriseComponentAttribute";
import enterpriseComponentMetadataClassFactory from "../../metadata/EnterpriseComponentMetadataClassFactory";
import EnterpriseComponentMetadata, {IAttributeDictionary} from "../../metadata/ec/EnterpriseComponentMetadata";
import EnterpriseComponentAttributeMetadata from "../../metadata/ec/EnterpriseComponentAttributeMetadata";
import EnterpriseObject from "../eo/EnterpriseObject";
import Relationship from "../relationship/Relationship";

type SortSpecification = {
    Direction: string,
    DisplayName: string,
    SortAttribute: string
}

class EnterpriseComponent {
    static MODE_NEW: number = 100;
    static MODE_QUERY: number = 101;
    static MODE_IDLE: number = 102;
    static MODE_RESULTS: number = 103;
    static MODE_DELETE: number = 104;
    static MODE_UPDATE: number = 105;

    visibilityMode: string | null = null;
    visibilityValue: string | null = null;
    attributes: IAttributeDictionary<EnterpriseComponentAttribute> = {};
    recordPointer: number = -1;
    pagePointer: number = -1;
    enterpriseObject: EnterpriseObject | null = null;
    isComponentValid: boolean = false;
    resultsBuffer: Map<number, Map<string, any>[]> = new Map();
    queryBuffer: Map<number, Map<string, any>[]> = new Map();
    recordWillChangeListeners: object[] = [];
    recordChangedListeners: object[] = [];
    invalidListeners: object[] = [];
    singleRecordRefreshStartedListeners: object[] = [];
    singleRecordRefreshCompletedListeners: object[] = [];
    insertRollbackCompletedListeners: object[] = [];
    loadStartedListeners: object[] = [];
    updateRollbackCompletedListeners: object[] = [];
    queryRollbackCompletedListeners: object[] = [];
    loadCompletedListeners: object[] = [];
    valueChangedListeners: object[] = [];
    updateStartedListeners: object[] = [];
    updateCompletedListeners: object[] = [];
    pickCompletedListeners: object[] = [];
    insertStartedListeners: object[] = [];
    insertCompletedListeners: object[] = [];
    deleteStartedListeners: object[] = [];
    deleteCompletedListeners: object[] = [];
    newQueryRecordListeners: object[] = [];
    newDeleteRecordListeners: object[] = [];
    newRecordListeners: object[] = [];
    newEditRecordListeners: object[] = [];
    modeChangedListeners: object[] = [];
    searchSpecification: IAttributeDictionary<String> = {};
    sortSpecification: SortSpecification[] = [];
    totalRowcount: number = -1;
    waiting: boolean = false;
    activeTimeout: NodeJS.Timeout | null = null;
    mode: number = EnterpriseComponent.MODE_IDLE;
    supportingData = {};
    idocRequest: string | undefined = undefined;
    idocResponse: string | undefined = undefined;
    error: string | null = null;
    serialNumber: string | undefined = undefined;
    companionEnterpriseObject: EnterpriseObject | null = null;

    store = {}

    enterpriseComponentMetadata: EnterpriseComponentMetadata;

    constructor(name: string) {
        this.serialNumber = uuidv4();

        let metadataClass:Object | undefined = enterpriseComponentMetadataClassFactory(name);
        // @ts-ignore
        this.enterpriseComponentMetadata = new (metadataClass)(name);

        let metadataAttributes: IAttributeDictionary<EnterpriseComponentAttributeMetadata> = this.enterpriseComponentMetadata.getAttributes();

        for (const key of Object.keys(metadataAttributes)) {
            let md = metadataAttributes[key];

            this.attributes[md.getName()] = new EnterpriseComponentAttribute(this, md);
        }
    };

    addRecordWillChangeListener = (newListener: any) => {
        // @ts-ignore
        if (!this.recordWillChangeListeners.includes(newListener)) {
            this.recordWillChangeListeners.push(newListener);
        }
    };

    addPickCompletedListener = (newListener: any) => {
        // @ts-ignore
        if (!this.pickCompletedListeners.includes(newListener)) {
            this.pickCompletedListeners.push(newListener);
        }
    };

    addRecordChangedListener = (newListener: any) => {
        // @ts-ignore
        if (!this.recordChangedListeners.includes(newListener)) {
            this.recordChangedListeners.push(newListener);
        }
    };

    addDeleteCompletedListener(newListener: any) {
        // @ts-ignore
        if (!this.deleteCompletedListeners.includes(newListener)) {
            this.deleteCompletedListeners.push(newListener);
        }
    };

    addDeleteStartedListener(newListener: any) {
        // @ts-ignore
        if (!this.deleteStartedListeners.includes(newListener)) {
            this.deleteStartedListeners.push(newListener);
        }
    };

    addInvalidListener(newListener: any) {
        // @ts-ignore
        if (!this.invalidListeners.includes(newListener)) {
            this.invalidListeners.push(newListener);
        }
    };

    addInsertRollbackCompletedListener(newListener: any) {
        // @ts-ignore
        if (!this.insertRollbackCompletedListeners.includes(newListener)) {
            this.insertRollbackCompletedListeners.push(newListener);
        }
    };

    addLoadCompletedListener(newListener: any) {
        // @ts-ignore
        if (!this.loadCompletedListeners.includes(newListener)) {
            this.loadCompletedListeners.push(newListener);
        }
    };

    addModeChangedListener(newListener: any) {
        // @ts-ignore
        if (!this.modeChangedListeners.includes(newListener)) {
            this.modeChangedListeners.push(newListener);
        }
    };

    addValueChangedListener(newListener: any) {
        // @ts-ignore
        if (!this.valueChangedListeners.includes(newListener)) {
            this.valueChangedListeners.push(newListener);
        }
    };

    addLoadStartedListener(newListener: any) {
        // @ts-ignore
        if (!this.loadStartedListeners.includes(newListener)) {
            this.loadStartedListeners.push(newListener);
        }
    };

    addNewQueryRecordListener(newListener: any) {
        // @ts-ignore
        if (!this.newQueryRecordListeners.includes(newListener)) {
            this.newQueryRecordListeners.push(newListener);
        }
    };

    addNewEditRecordListener(newListener: any) {
        // @ts-ignore
        if (!this.newEditRecordListeners.includes(newListener)) {
            this.newEditRecordListeners.push(newListener);
        }
    };

    addNewRecordListener(newListener: any) {
        // @ts-ignore
        if (!this.newRecordListeners.includes(newListener)) {
            this.newRecordListeners.push(newListener);
        }
    };

    addNewDeleteRecordListener(newListener: any) {
        // @ts-ignore
        if (!this.newDeleteRecordListeners.includes(newListener)) {
            this.newDeleteRecordListeners.push(newListener);
        }
    };

    addSingleRecordRefreshStartedListener(newListener: any) {
        // @ts-ignore
        if (!this.singleRecordRefreshStartedListeners.includes(newListener)) {
            this.singleRecordRefreshStartedListeners.push(newListener);
        }
    };

    addSingleRecordRefreshCompletedListener(newListener: any) {
        // @ts-ignore
        if (!this.singleRecordRefreshCompletedListeners.includes(newListener)) {
            this.singleRecordRefreshCompletedListeners.push(newListener);
        }
    };

    addUpdateCompletedListener(newListener: any) {
        // @ts-ignore
        if (!this.updateCompletedListeners.includes(newListener)) {
            this.updateCompletedListeners.push(newListener);
        }
    };

    addUpdateRollbackCompletedListener(newListener: any) {
        // @ts-ignore
        if (!this.updateRollbackCompletedListeners.includes(newListener)) {
            this.updateRollbackCompletedListeners.push(newListener);
        }
    };

    addUpdateStartedListener(newListener: any) {
        // @ts-ignore
        if (!this.updateStartedListeners.includes(newListener)) {
            this.updateStartedListeners.push(newListener);
        }
    };

    addInsertCompletedListener(newListener: any) {
        // @ts-ignore
        if (!this.insertCompletedListeners.includes(newListener)) {
            this.insertCompletedListeners.push(newListener);
        }
    };

    addInsertStartedListener(newListener: any) {
        // @ts-ignore
        if (!this.insertStartedListeners.includes(newListener)) {
            this.insertStartedListeners.push(newListener);
        }
    };

    addQueryRollbackCompletedListener(newListener: any) {
        // @ts-ignore
        if (!this.queryRollbackCompletedListeners.includes(newListener)) {
            this.queryRollbackCompletedListeners.push(newListener);
        }
    };

    canChangeAttributeValue(attributeName: string): boolean {
        if (this.mode === EnterpriseComponent.MODE_IDLE) {
            return false;
        }

        let attribute = this.getAttributes()[attributeName];

        if (attribute === null) {
            return false;
        }

        return !(this.mode === EnterpriseComponent.MODE_RESULTS && this.getBuffer().size === 0);
    };

    clearAttribute(attributeName: string) {
        if (!this.canChangeAttributeValue(attributeName)) {
            return false;
        }

        let row: Map<string, any> = this.getBuffer().get(this.getPagePointer())![this.getRecordPointer()]

        row.set("__operation", "query results");

        row.set(attributeName, null);

        if (row.get(attributeName + "_original") !== undefined) {
            row.delete(attributeName + "_original");
        }
        if (row.get(attributeName + "_old") !== undefined) {
            row.delete(attributeName + "_old");
        }

        this.notifyValueChangedListeners();
    };

    deleteEnterpriseComponentFailed() {
        EventBus.error.emit("show-system-error");
    };

    deleteEnterpriseComponentSuccess() {
        if (this.getBuffer().size > 0 && this.getBuffer().get(this.getPagePointer())!.length > 0) {
            this.getBuffer().get(this.getPagePointer())!.splice(this.getRecordPointer(), 1);
            if (this.getBuffer().get(this.getPagePointer())!.length > 0) {
                if (this.getRecordPointer() > 0) this.recordPointer = this.recordPointer - 1;
            }
            else this.recordPointer = -1;
        }
        if (this.getBuffer().size > 0 && this.getBuffer().get(this.getPagePointer())!.length > 0) {
            this.getBuffer().get(this.getPagePointer())![this.getRecordPointer()].set("RowSelected", true);
        }
        if (this.getBuffer().size === 1 && this.getBuffer().get(this.getPagePointer())!.length === 0) {
            this.totalRowcount = 0;
        } else {
            this.totalRowcount -= 1;
        }
        this.setMode(EnterpriseComponent.MODE_RESULTS);

        this.isComponentValid = true;

        this.notifyDeleteCompletedListeners();
    };

    executeDelete() {
        this.performDelete();
    };

    executeQuery = (page: number, pageSize: number) => {
        if (this.waiting) {
            if (this.activeTimeout !== null) {
                clearTimeout(this.activeTimeout);
            }
            this.activeTimeout = setTimeout(() => {
                this.executeQuery(page, pageSize);
            }, 1000);
            return;
        }
        this.waiting = true;

        let foreignKey = null;
        if (this.getEnterpriseObject() === null) {
            if (this.companionEnterpriseObject !== null) {
                let companionEnterpriseComponent = this.companionEnterpriseObject.getEnterpriseComponent(this.getName())!;
                let relationship: Relationship | undefined = companionEnterpriseComponent.getEnterpriseObject()!.getRelationshipForChildEnterpriseComponent(companionEnterpriseComponent);
                if (relationship !== undefined) {
                    let parentEnterpriseComponent: EnterpriseComponent = relationship.getParentEnterpriseComponent()!;
                    if (parentEnterpriseComponent!.getRecordPointer() !== -1) {
                        let p: Map<string, any>[] = parentEnterpriseComponent!.getBuffer().get(parentEnterpriseComponent!.getPagePointer())!

                        foreignKey = p[parentEnterpriseComponent.getRecordPointer()].get(relationship.getSourceAttribute());
                    }
                }
            }
            this._executeQuery(page, pageSize, foreignKey);
        } else {
            let relationship: Relationship | undefined = this.getEnterpriseObject()!.getRelationshipForChildEnterpriseComponent(this);
            if (relationship === undefined) {
                this._executeQuery(page, pageSize, foreignKey);
            } else {
                let parentEnterpriseComponent: EnterpriseComponent = relationship.getParentEnterpriseComponent()!;

                if (!parentEnterpriseComponent!.isValid()) {
                    this.waiting = false;
                    //this.notifyLoadCompletedListeners();
                    return;
                }

                if (parentEnterpriseComponent!.getBuffer().size === 0) {
                    this.waiting = false;
                    this.isComponentValid = true;
                    this.invalidateChildren();
                    this.notifyLoadCompletedListeners();
                } else {
                    if (parentEnterpriseComponent!.getRecordPointer() !== -1) {
                        let p: Map<string, any>[] = parentEnterpriseComponent!.getBuffer().get(parentEnterpriseComponent!.getPagePointer())!
                        
                        foreignKey = p[parentEnterpriseComponent.getRecordPointer()].get(relationship.getSourceAttribute());
                    }
                }

                if (foreignKey !== null) {
                    this._executeQuery(page, pageSize, foreignKey);
                }
            }
        }
    };

    _executeQuery = (page: number, pageSize: number, foreignKey: string | null) => {
        if (page === undefined || pageSize === undefined) {
            this.resultsBuffer = new Map();
            this.performQuery(1, 200, foreignKey);
        } else {
            if (this.mode === EnterpriseComponent.MODE_QUERY) {
                this.resultsBuffer = new Map();
                this.error = null;
                this.performQuery(page, pageSize, foreignKey);
                this.setMode(EnterpriseComponent.MODE_RESULTS);
            } else if (this.resultsBuffer.size === 0 && this.totalRowcount === 0) {
                this.isComponentValid = true;

                this.waiting = false;
                this.notifyLoadCompletedListeners();
            } else if (this.resultsBuffer.get(page) !== undefined) {
                this.isComponentValid = true;
                this.pagePointer = page;

                this.waiting = false;
                this.notifyLoadCompletedListeners();
            } else {
                if (this.pagePointer !== page) this.recordPointer = -1;
                this.setMode(EnterpriseComponent.MODE_QUERY);
                this.error = null;
                this.performQuery(page, pageSize, foreignKey);
                this.setMode(EnterpriseComponent.MODE_RESULTS);
            }
        }
    };

    executeInsert = () => {
        this.performInsert();
    };

    executeInsertRollback = () => {
        this.setMode(EnterpriseComponent.MODE_RESULTS);

        if (this.getBuffer().size > 0 && this.getBuffer().get(this.getPagePointer())!.length > 0) {
            let operation = this.resultsBuffer.get(this.getPagePointer())![this.getRecordPointer()].get("__operation");
            if (operation && operation === "new") {
                this.resultsBuffer.get(this.getPagePointer())!.splice(this.getRecordPointer(), 1);
            }

            if (this.getBuffer().size > 0 && this.getBuffer().get(this.getPagePointer())!.length > 0) {
                for (let i = 0; i < this.getBuffer().get(this.getPagePointer())!.length; i++) {
                    this.getBuffer().get(this.getPagePointer())![i].set("RowSelected", false);
                    this.getBuffer().get(this.getPagePointer())![i].set("RowNum", i);
                }

                this.getBuffer().get(this.getPagePointer())![this.getRecordPointer()].set("RowSelected", true);
            } else {
                this.setRecordPointer(-1);
            }
        } else {
            this.setRecordPointer(-1);
        }
        this.notifyInsertRollbackCompletedListeners();
    };

    executeQueryRollback = () => {
        this.setMode(EnterpriseComponent.MODE_RESULTS);

        this.notifyQueryRollbackCompletedListeners();
    };

    executeSingleRecordRefresh() {
        if (this.singleRecordRefreshCompletedListeners.length === 0) {
            return;
        }

        if (this.waiting) {
            if (this.activeTimeout !== null) {
                clearTimeout(this.activeTimeout);
            }
            this.activeTimeout = setTimeout(() => {
                let record: Map<string, any> | undefined = this.getCurrentRecord();
                if (record) this.performSingleRecordRefresh(record.get("USID"));
            }, 1000);
            return;
        }

        let record = this.getCurrentRecord();
        if (record) {
            this.waiting = true;
            this.performSingleRecordRefresh(record.get("USID"));
        }
    };

    executeUpdate = () => {
        this.performUpdate();

        let row: Map<string, any> | undefined = this.getCurrentRecord();
        if (row === undefined) return;

        for (const key of Object.keys(this.getAttributes())) {
            let attribute = this.getAttributes()[key];

            if (row.get(attribute.getName() + "_original") !== undefined) {
                row.delete(attribute.getName() + "_original");
                row.delete(attribute.getName() + "_old");
            }
        }
        row.set("__operation", "query results");
    };

    executeUpdateRollback() {
        this.setMode(EnterpriseComponent.MODE_RESULTS);

        let row = this.getCurrentRecord();
        if (row === undefined) return;

        for (const key of Object.keys(this.getAttributes())) {
            let attribute: EnterpriseComponentAttribute = this.getAttributes()[key];

            if (row.get(attribute.getName() + "_original") !== undefined) {
                row.set(attribute.getName(), row.get(attribute.getName() + "_original"));
                row.delete(attribute.getName() + "_original");
                row.delete(attribute.getName() + "_old");
            }
        }
        row.set("__operation", "query results");

        this.notifyUpdateRollbackCompletedListeners();
    };

    fetchEnterpriseComponentFailed() {
        EventBus.error.emit("show-system-error");
    };

    fetchEnterpriseComponentSuccess(data: any, page: number, pageSize: number) {
        if (data["Results"].length !== 0) {
            let existingRows: Map<string, any>[] | undefined = this.resultsBuffer.get(page);
            if (existingRows) {
                let newArray: Map<string, any>[] = existingRows.concat(data["Results"]);
                this.resultsBuffer.set(page, newArray);
            } else {
                let newPage: Map<string, any>[] = [];
                for (let i = 0; i < data.Results.length; i++) {
                    let newRow = new Map<string, any>()
                    for (let key of Object.keys(data.Results[i])) {
                        newRow.set(key, data.Results[i][key])
                    }
                    newPage.push(newRow)
                }
                this.resultsBuffer.set(page, newPage);
            }

            let b: Map<string, any>[] = this.resultsBuffer.get(page)!;
            for (let i = 0; i < b.length; i++) {
                b[i].set("RowNum", i);
            }
        }

        if (data["Error"] !== undefined) {
            this.error = data["Error"];
        }

        if (data["Count"] === undefined) {
            if (data["Results"].length < pageSize) {
                this.totalRowcount = this.resultsBuffer.size;
            } else {
                this.totalRowcount = -1;
            }
        } else {
            let count: any = data["Count"];
            if (count) {
                this.totalRowcount = parseInt(count);
            }
        }

        this.isComponentValid = true;

        this.pagePointer = page;

        this.mode = EnterpriseComponent.MODE_RESULTS;

        if (this.recordPointer < 1) {
            if (data["Results"].length > 0) {
                this.recordPointer = 0;
                this.resultsBuffer.get(this.getPagePointer())![0].set("RowSelected", true);
            }
            if (this.getEnterpriseObject() !== null) {
                let childEnterpriseComponents: Map<string, EnterpriseComponent> = this.getEnterpriseObject()!.getChildEnterpriseComponents(this);

                for (let key of childEnterpriseComponents.keys()) {
                    let childEnterpriseComponent: EnterpriseComponent | undefined = childEnterpriseComponents.get(key);

                    if (childEnterpriseComponent) {
                        for (let j = 0; j < data["Results"].length; j++) {
                            let childDataResults = data["Results"][j]["ListOf" + key];

                            if (childDataResults !== undefined) {
                                childEnterpriseComponent.fetchEnterpriseComponentSuccess(childDataResults, 1, 1000);
                            } else {
                                childEnterpriseComponent.invalidate();
                            }
                        }
                    }
                }
            }
            this.notifyLoadCompletedListeners();
        } else {
            this.notifyLoadCompletedListeners();
            this.nextRecord();
        }
        this.waiting = false;
    };

    firstRecord(enable_events = true) {
        if (this.hasEnterpriseComponentBeenModified()) {
            return false;
        }

        if (!this.isValid()) {
            return false;
        }

        if (this.getBuffer().size === 0) return false;

        this.recordPointer = 0;
        this.pagePointer = 1;

        let row: Map<string, any> | undefined = this.getCurrentRecord();
        if (row === undefined) return false;

        if (row.get("RowSelected") === true) {
            row.set("RowSelected", false);
        }

        let found: boolean = false;
        if (this.enterpriseObject !== null) {
            let relationship: Relationship | undefined = this.enterpriseObject.getRelationshipForChildEnterpriseComponent(this);
            if (relationship) {
                let value: any = this.getSourceAttributeValueForParent();
                for (let i = 0; i < this.getBuffer().get(this.getPagePointer())!.length; i++) {
                    let page: Map<string, any>[] | undefined = this.getBuffer().get(this.getPagePointer())
                    if (page && page![this.recordPointer].get(relationship.getDestinationAttribute()) === value) {
                        found = true;
                        break;
                    } else {
                        this.recordPointer++;
                    }
                }
            } else {
                found = true;
            }
        } else {
            found = true;
        }

        if (found) {
            this.getBuffer().get(this.getPagePointer())![this.recordPointer].set("RowSelected", true);
            if (enable_events) this.notifyRecordChangedListeners();
            this.invalidateChildren();
            return true;
        }

        return false;
    };

    forceAttributeValue = (attributeName: string, attributeValue: any) => {
        if (!this.canChangeAttributeValue(attributeName)) {
            return false;
        }

        let row: Map<string, any> | undefined = this.getCurrentRecord();
        if (row === undefined) return false;

        if (attributeValue === "NULL") {
            attributeValue = null;
        }
        row.set(attributeName, attributeValue);

        row.set("__operation", "update");
        row.set(attributeName + "_original", attributeValue);
        row.set(attributeName + "_old", attributeValue);

        this.notifyValueChangedListeners();
    };

    getAttributes() {
        return this.attributes;
    };

    getAttribute(name: string) {
        return this.getAttributes()[name];
    };

    getAttributeValue(attributeName: string, type?: string) {
        if (this.getRecordPointer() === -1) return null;

        let value;
        let row: Map<string, any>;

        let suffix: string = "";

        if (type === "original") {
            suffix = "_original";
        }

        if (this.mode === EnterpriseComponent.MODE_QUERY) {
            row = this.getBuffer().get(this.getPagePointer())![0];
            value = row.get(attributeName + suffix);
        } else {
            row = this.getBuffer().get(this.getPagePointer())![this.getRecordPointer()];
            value = row.get(attributeName + suffix);
        }

        return value;
    };

    getAttributeValueAsString = (attributeName: string, type: string) => {
        let value = this.getAttributeValue(attributeName, type);
        let out;

        if (value === null || value === undefined) {
            out = null;
        } else {
            out = value.toString();
        }
        return out;
    };

    getQueryAttributeValue(attributeName: string, type?: string) {
        if (this.getRecordPointer() === -1) return null;

        let value;
        let row: Map<string, any>;

        let suffix: string = "";

        if (type === "original") {
            suffix = "_original";
        }

        row = this.queryBuffer.get(this.getPagePointer())![0];
        value = row.get(attributeName + suffix);

        return value;
    };

    getQueryAttributeValueAsString = (attributeName: string, type: string) => {
        let value = this.getQueryAttributeValue(attributeName, type);
        let out;

        if (value === null || value === undefined) {
            out = null;
        } else {
            out = value.toString();
        }
        return out;
    };

    getBuffer(): Map<number, Map<string, any>[]> {
        if (this.mode === EnterpriseComponent.MODE_QUERY) {
            return this.queryBuffer;
        } else if (this.mode === EnterpriseComponent.MODE_RESULTS) {
            return this.resultsBuffer;
        } else if (this.mode === EnterpriseComponent.MODE_NEW) {
            return this.resultsBuffer;
        } else if (this.mode === EnterpriseComponent.MODE_IDLE) {
            return this.resultsBuffer;
        } else if (this.mode === EnterpriseComponent.MODE_UPDATE) {
            return this.resultsBuffer;
        } else if (this.mode === EnterpriseComponent.MODE_DELETE) {
            return this.resultsBuffer;
        }
        return new Map<number, Map<string, any>[]>();
    };

    getCurrentRecord(): Map<string, any> | undefined {
        let page: Map<string, any>[] | undefined = this.getBuffer().get(this.getPagePointer());
        if (!page) return undefined;

        try {
            return page[this.getRecordPointer()];
        } catch (error) {
            return undefined;
        }
    };

    getDefaultCacheSize() {
        return this.enterpriseComponentMetadata.defaultCacheSize;
    };

    getEnterpriseObject(): EnterpriseObject | null {
        return this.enterpriseObject;
    };

    getError() {
        return this.error;
    };

    getFormattedSortSpecification() {
        let out: string | null = "";
        for (let i: number = 0; i < this.getSortSpecification().length; i++) {
            let sortSpec: SortSpecification = this.getSortSpecification()[i];

            if (i !== 0) {
                out = out + ",";
            }
            out = out + sortSpec.SortAttribute + ":" + sortSpec.Direction;
        }

        if (out === "") out = null;

        return out;
    };

    getIDOCRequest() {
        return this.idocRequest;
    };

    getIDOCResponse = () => {
        return this.idocResponse;
    };

    getMode = () => {
        return this.mode;
    };

    getName(): string {
        return this.enterpriseComponentMetadata.getName();
    };

    getPagePointer() {
        if (this.mode === EnterpriseComponent.MODE_QUERY) {
            return 1;
        } else if (this.mode === EnterpriseComponent.MODE_NEW) {
            return 1;
        }
        return this.pagePointer;
    };

    getParentEnterpriseComponent(): EnterpriseComponent | undefined {
        if (this.getEnterpriseObject() === null) {
            return undefined;
        }

        let relationship: Relationship | undefined = this.getEnterpriseObject()!.getRelationshipForChildEnterpriseComponent(this);

        if (relationship) {
            return relationship.getParentEnterpriseComponent();
        }
        return undefined;
    };

    getRecordPointer(): number {
        return this.recordPointer;
    };

    getSearchSpecification() {
        return this.searchSpecification;
    };

    getSelectedRecordNumber() {
        if (this.getBuffer().size !== 0) {
            for (let i = 0; i < this.getBuffer().get(this.getPagePointer())!.length; i++) {
                // @ts-ignore
                if (this.getBuffer().get(this.getPagePointer())![i].get("RowSelected") === true) {
                    return i;
                }
            }
        }
        return -1;
    };

    getSerialNumber() {
        return this.serialNumber;
    };

    getSortSpecification() {
        return this.sortSpecification || [];
    };

    getSourceAttributeValueForParent() {
        let out: any = undefined;

        let parentEnterpriseComponent: EnterpriseComponent | undefined = this.getParentEnterpriseComponent();
        if (parentEnterpriseComponent === undefined) {
            return out;
        }

        let parentECRecordPointer = parentEnterpriseComponent.getRecordPointer();
        let relationship: Relationship = this.getEnterpriseObject()!.getRelationshipForChildEnterpriseComponent(this)!;
        let parentPage: Map<string, any>[] = parentEnterpriseComponent?.getBuffer().get(parentEnterpriseComponent.getPagePointer())!
        let row: Map<string, any> = parentPage[parentECRecordPointer];
        return row.get(relationship.getSourceAttribute());
    };

    getStore = () => {
        return this.store;
    }

    getTotalRowcount = () => {
        return this.totalRowcount;
    };

    getVisibilityMode = () => {
        return this.visibilityMode;
    };

    getVisibilityValue = () => {
        return this.visibilityValue;
    };

    hasEnterpriseComponentBeenModified = () => {
        if (this.getBuffer().size === 0) {
            return false;
        }

        let page: Map<string, any>[] | undefined = this.getBuffer().get(this.getPagePointer());
        if (page === undefined) {
            return false;
        }

        for (let i = 0; i < page.length; i++) {
            let row: Map<string, any> = page[this.getRecordPointer()];

            if (row.get("__operation") !== "query results" && row.get("__operation") !== "delete") {
                return true;
            }

            /*
            for child in self.get_enterprise_object().get_child_enterprise_components(self).values() {
                if child.has_enterprise_component_been_modified() {
                    return true;
                }
                return false;
            }
            */
        }
        return false;
    };

    insertEnterpriseComponentFailed = () => {
        EventBus.error.emit("show-system-error");
    };

    insertEnterpriseComponentSuccess = (data: any) => {
        if (data.Results !== undefined && data.Results.length >= 1) {
            let newResults: any[] = []
            for (let i: number = 0; i < data.Results.length; i++) {
                let page: Map<string, any>[] = this.getBuffer().get(this.getPagePointer())!;
                for (let j: number = 0; j < page.length; j++) {
                    if (data.Results[i]["USID"] === page[j].get("USID") && page[j].get("__operation") === "new") {
                        newResults.push(data.Results[i]);
                        break;
                    }
                }
            }
            data.Results = newResults;
        }

        this.totalRowcount += 1;

        for (let i = 0; i < this.getBuffer().get(this.getPagePointer())!.length; i++) {
            let row: Map<string, any> = this.getBuffer().get(this.getPagePointer())![i];

            row.set("RowNum", i);
            row.set("__operation", "query results");
        }

        let row = this.getBuffer().get(this.getPagePointer())![this.getRecordPointer()];

        let keys = Object.keys(this.getAttributes());
        for (let i = 0; i < keys.length; i++) {
            let key = keys[i];
            let attribute = this.getAttributes()[key];
            row.set(attribute.getName(), data.Results[0][attribute.getName()]);
            row.delete(attribute.getName() + "_old");
            row.delete(attribute.getName() + "_original");
        }

        this.setMode(EnterpriseComponent.MODE_RESULTS);

        this.isComponentValid = true;

        this.getBuffer().get(this.getPagePointer())![this.getRecordPointer()].set("RowSelected", true);

        this.notifyInsertCompletedListeners();
    };

    invalidate() {
        this.recordPointer = -1;
        this.resultsBuffer = new Map();
        this.queryBuffer = new Map();
        this.isComponentValid = false;
        this.totalRowcount = -1;
        this.setMode(EnterpriseComponent.MODE_IDLE);
        this.error = null;

        this.invalidateChildren();

        this.notifyInvalidListeners();
    };

    invalidateChildren() {
        if (this.enterpriseObject !== null) {
            let children: Map<string, EnterpriseComponent> = this.enterpriseObject.getChildEnterpriseComponents(this);
            for (let key of children.keys()) {
                let childEnterpriseComponent: EnterpriseComponent = children.get(key)!;
                let relationship: Relationship | undefined = this.getEnterpriseObject()!.getRelationshipForChildEnterpriseComponent(childEnterpriseComponent);
                if (relationship !== undefined) {
                    if (relationship.getInvalidateChildren() === null || relationship.getInvalidateChildren() === undefined || relationship.getInvalidateChildren()) {
                        childEnterpriseComponent.invalidate();
                    }
                }
            }
        }
    };

    isEmpty = () => {
        if (this.getRecordPointer() === -1) return true;

        if (!this.isValid()) {
            return true;
        }

        if (this.getBuffer().size === 0) return true;

        return this.getBuffer().get(0)?.length === 0;
    };

    isDeleteMode = () => {
        return this.mode === EnterpriseComponent.MODE_DELETE;
    };

    isQueryMode = () => {
        return this.mode === EnterpriseComponent.MODE_QUERY;
    };

    isNewMode = () => {
        return this.mode === EnterpriseComponent.MODE_NEW;
    };

    isViewMode = () => {
        return this.mode === EnterpriseComponent.MODE_RESULTS;
    };

    isPrimaryEnterpriseComponent = () => {
        return this.getParentEnterpriseComponent() === null;
    };

    isUpdateMode = () => {
        return this.mode === EnterpriseComponent.MODE_UPDATE;
    };

    isValid = () => {
        return this.isComponentValid;
    };

    isLastRecord = () => {
        return this.getRecordPointer() === this.totalRowcount - 1;
    };

    lastRecord(enable_events = true) {
        if (this.hasEnterpriseComponentBeenModified()) {
            return false;
        }

        if (!this.isValid()) {
            return false;
        }

        let out;
        /*
        let page: Map<string, any>[] | undefined = this.getBuffer().get(this.getPagePointer())
        if (!page || page!.length === 0 || this.getRecordPointer() === page!.length - 1) {
           out = false;
        } else {
            if (this.getRecordPointer() && this.getBuffer()[this.getRecordPointer()].RowSelected === true) {
                this.getBuffer()[this.getRecordPointer()].RowSelected = false;
            }

            this.recordPointer = this.getBuffer().size - 1;
            this.getBuffer()[this.recordPointer].RowSelected = true;
            if (enable_events) this.notifyRecordChangedListeners();
            out = true;
        }
        */
        if (out) this.invalidateChildren();

        return out;
    };

    newQuery = () => {
        this.setMode(EnterpriseComponent.MODE_QUERY);
        this.queryBuffer = new Map<number, Map<string, any>[]>();

        this.queryBuffer.set(1, []);
        let initialRow = new Map<string, any>();
        initialRow.set("__operation", "query")
        this.queryBuffer.set(1, [initialRow])
        this.recordPointer = 0;
        this.searchSpecification = {};
        this.sortSpecification = [];
        this.error = null;
        this.notifyNewQueryRecordListeners();
    };

    newDelete = () => {
        this.setMode(EnterpriseComponent.MODE_DELETE);
        let initialRow = new Map<string, any>();
        initialRow.set("__operation", "delete")
        this.queryBuffer.set(1, [initialRow])
        this.isComponentValid = true;
        this.error = null;
        this.notifyNewDeleteRecordListeners();
    };

    newEdit = () => {
        this.setMode(EnterpriseComponent.MODE_UPDATE);

        this.notifyNewEditRecordListeners();
    };

    newRecord = () => {
        this.setMode(EnterpriseComponent.MODE_NEW);
        this.error = null;

        let row: Map<string, any> = new Map();

        let keys: string[] = Object.keys(this.getAttributes());
        for (let i = 0; i < keys.length; i++) {
            let attribute = this.getAttributes()[keys[i]];
            row.set(attribute.getName(), attribute.getDefaultValue());
        }

        row.set("USID", uuidv4());
        row.set("RowSelected", true);
        row.set("RowNum", 0);
        row.set("__operation", "new");

        let foreignKey = null;
        let destinationAttribute: string | undefined = undefined;
        let realm = null;
        if (this.getEnterpriseObject() !== null) {
            let relationship: Relationship | undefined = this.getEnterpriseObject()!.getRelationshipForChildEnterpriseComponent(this);
            if (relationship !== undefined) {
                destinationAttribute = relationship.getDestinationAttribute();

                let parentEnterpriseComponent: EnterpriseComponent = relationship.getParentEnterpriseComponent()!;

                if (parentEnterpriseComponent.isValid()) {
                    if (parentEnterpriseComponent.getBuffer().size !== 0) {
                        if (parentEnterpriseComponent.getRecordPointer() !== -1) {
                            foreignKey = parentEnterpriseComponent.getBuffer().get(parentEnterpriseComponent.getPagePointer())![parentEnterpriseComponent.getRecordPointer()].get(relationship.getSourceAttribute());
                            realm = parentEnterpriseComponent.getBuffer().get(parentEnterpriseComponent.getPagePointer())![parentEnterpriseComponent.getRecordPointer()].get("Realm");
                        }
                    }
                }
            }
        }

        if (foreignKey !== null) {
            row.set(destinationAttribute!, foreignKey);
            row.set("Realm", realm);
        }

        if (this.getBuffer().size > 0 && this.getBuffer().get(this.getPagePointer())!.length > 0) {
            for (let i = 0; i < this.getBuffer().get(this.getPagePointer())!.length; i++) {
                this.getBuffer().get(this.getPagePointer())![i].set("RowSelected", false);
            }
        }
        if (this.getRecordPointer() === -1) {
            this.setRecordPointer(0);
        }

        if (this.getBuffer().size === 0) {
            this.setPagePointer(1);
            this.getBuffer().set(this.getPagePointer(), [row]);
        } else {
            this.getBuffer().get(this.getPagePointer())!.splice(this.getRecordPointer(), 0, row);
        }

        if (this.getBuffer().size > 0 && this.getBuffer().get(this.getPagePointer())!.length > 0) {
            for (let i = 0; i < this.getBuffer().get(this.getPagePointer())!.length; i++) {
                this.getBuffer().get(this.getPagePointer())![i].set("RowNum", i);
            }
        }

        this.isComponentValid = true;

        this.notifyNewRecordListeners();
    };

    nextRecord = (enable_events = true) => {
        if (this.hasEnterpriseComponentBeenModified()) {
            return false;
        }

        if (!this.isValid()) {
            return false;
        }

        let out;
        let page: Map<string, any>[] | undefined = this.getBuffer().get(this.getPagePointer())
        if (!page || page!.length === 0 || this.getRecordPointer() === page!.length - 1) {
            out = false;
        } else {
            let a: any = page![this.getRecordPointer()].get("RowSelected");

            if (this.getRecordPointer() !== -1 && a === true) {
                this.getBuffer().get(this.getPagePointer())![this.getRecordPointer()].set("RowSelected", false);
            }

            let found = false;
            if (this.enterpriseObject !== null) {
                let relationship = this.enterpriseObject.getRelationshipForChildEnterpriseComponent(this);
                if (relationship) {
                    let value = this.getSourceAttributeValueForParent();
                    for (let i = this.recordPointer + 1; i < this.getBuffer().get(this.getPagePointer())!.length; i++) {
                        if (this.getBuffer().get(this.getPagePointer())![i].get(relationship.getDestinationAttribute()) === value) {
                            found = true;
                            this.recordPointer = i;
                            break;
                        } else {
                            this.recordPointer++;
                        }
                    }
                } else {
                    this.recordPointer++;
                    found = true;
                }
            } else {
                this.recordPointer++;
                found = true;
            }

            if (found) {
                page![this.recordPointer].set("RowSelected", true);
                if (enable_events) this.notifyRecordChangedListeners();
                this.invalidateChildren();
                out = true;
            } else {
                out = false;
            }
        }

        return out;
    };

    notifyDeleteStartListeners = () => {
        for (let i = 0; i < this.deleteStartedListeners.length; i++) {
            let listener = this.deleteStartedListeners[i];
            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentDeleteStarted(this)
            }
        }
    };

    notifyDeleteCompletedListeners = () => {
        for (let i = 0; i < this.deleteCompletedListeners.length; i++) {
            let listener = this.deleteCompletedListeners[i];
            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentDeleteCompleted(this)
            }
        }
    };

    notifyInsertCompletedListeners = () => {
        for (let i = 0; i < this.insertCompletedListeners.length; i++) {
            let listener = this.insertCompletedListeners[i];
            // @ts-ignore
            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentInsertCompleted(this)
            }
        }
    };

    notifyInsertRollbackCompletedListeners = () => {
        for (let i = 0; i < this.insertRollbackCompletedListeners.length; i++) {
            let listener = this.insertRollbackCompletedListeners[i];
            // @ts-ignore
            listener.enterpriseComponentInsertRollbackCompleted(this);
        }
    };

    notifyInsertStartListeners = () => {
        for (let i = 0; i < this.insertStartedListeners.length; i++) {
            let listener = this.insertStartedListeners[i];
            // @ts-ignore
            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentInsertStarted(this)
            }
        }
    };

    notifyInvalidListeners = () => {
        for (let i = 0; i < this.invalidListeners.length; i++) {
            let listener = this.invalidListeners[i];
            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentInvalidated(this)
            }
        }
    };

    notifyLoadCompletedListeners = () => {
        for (let i = 0; i < this.loadCompletedListeners.length; i++) {
            let listener = this.loadCompletedListeners[i];
            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentLoadCompleted(this)
            }
        }
    };

    notifyLoadStartListeners = () => {
        this.isComponentValid = false;

        for (let i = 0; i < this.loadStartedListeners.length; i++) {
            let listener: object = this.loadStartedListeners[i];
            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentLoadStarted(this)
            }
        }
    };

    notifyModeChangedListeners = () => {
        this.isComponentValid = false;

        for (let i = 0; i < this.modeChangedListeners.length; i++) {
            let listener: object = this.modeChangedListeners[i];
            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentModeChanged(this)
            }
        }
    };

    notifyNewDeleteRecordListeners = () => {
        for (let i = 0; i < this.newDeleteRecordListeners.length; i++) {
            let listener: object = this.newDeleteRecordListeners[i];
            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentNewDeleteRecordStarted(this)
            }
        }
    };

    notifyNewEditRecordListeners = () => {
        for (let i = 0; i < this.newEditRecordListeners.length; i++) {
            let listener: object = this.newEditRecordListeners[i];
            // @ts-ignore
            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentNewEditRecordStarted(this)
            }
        }
    };

    notifyNewQueryRecordListeners = () => {
        for (let i = 0; i < this.newQueryRecordListeners.length; i++) {
            let listener: object = this.newQueryRecordListeners[i];
            // @ts-ignore
            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentNewQueryRecordStarted(this)
            }
        }
    };

    notifyNewRecordListeners = () => {
        for (let i = 0; i < this.newRecordListeners.length; i++) {
            let listener: object = this.newRecordListeners[i];
            // @ts-ignore
            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentNewRecordStarted(this)
            }
        }
    };

    notifyPickCompletedListeners = () => {
        for (let i = 0; i < this.pickCompletedListeners.length; i++) {
            let listener: object = this.pickCompletedListeners[i];
            // @ts-ignore
            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentPickCompleted(this)
            }
        }
    };

    notifyQueryRollbackCompletedListeners = () => {
        for (let i = 0; i < this.queryRollbackCompletedListeners.length; i++) {
            let listener = this.queryRollbackCompletedListeners[i];

            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentQueryRollbackCompleted(this)
            }
        }
    };

    notifyRecordWillChangeListeners = () => {
        let okToChange = true;
        for (let i = 0; i < this.recordWillChangeListeners.length; i++) {
            let listener = this.recordWillChangeListeners[i];
            // @ts-ignore
            okToChange = okToChange && listener.enterpriseComponentRecordWillChange(this);
        }
        return okToChange;
    };

    notifyRecordChangedListeners = () => {
        for (let i = 0; i < this.recordChangedListeners.length; i++) {
            let listener = this.recordChangedListeners[i];

            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentRecordChanged(this)
            }
        }
    };

    notifySingleRecordRefreshStartListeners = () => {
        for (let i = 0; i < this.singleRecordRefreshStartedListeners.length; i++) {
            let listener = this.singleRecordRefreshStartedListeners[i];

            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentSingleRecordRefreshStarted(this)
            }
        }
    };

    notifySingleRecordRefreshCompletedListeners = () => {
        for (let i = 0; i < this.singleRecordRefreshCompletedListeners.length; i++) {
            let listener = this.singleRecordRefreshCompletedListeners[i];

            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentSingleRecordRefreshCompleted(this)
            }
        }
    };

    notifyUpdateStartListeners = () => {
        for (let i = 0; i < this.updateStartedListeners.length; i++) {
            let listener = this.updateStartedListeners[i];

            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentUpdateStarted(this)
            }
        }
    };

    notifyUpdateCompletedListeners = () => {
        for (let i = 0; i < this.updateCompletedListeners.length; i++) {
            let listener = this.updateCompletedListeners[i];

            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentUpdateCompleted(this)
            }
        }
    };

    notifyUpdateRollbackCompletedListeners = () => {
        for (let i = 0; i < this.updateRollbackCompletedListeners.length; i++) {
            let listener = this.updateRollbackCompletedListeners[i];

            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentUpdateRollbackCompleted(this)
            }
        }
    };

    notifyValueChangedListeners = () => {
        for (let i = 0; i < this.valueChangedListeners.length; i++) {
            let listener = this.valueChangedListeners[i];

            try {
                // @ts-ignore
                listener(this);
            } catch (error) {
                // @ts-ignore
                listener.enterpriseComponentValueChanged(this)
            }
        }
    };

    performDelete() {
    };

    performInsert() {
    };

    performQuery(page?: number, pageSize?: number, foreignKey?: string | null) {
    };

    performSingleRecordRefresh(primaryKey: string){
    };

    performUpdate() {
    };

    preparePUTPayload() {
        let out: Map<string, any> = new Map<string, any>();

        let page: Map<string, any>[] | undefined = this.getBuffer().get(this.getPagePointer());

        out.set("USID", page![this.getRecordPointer()].get("USID"));

        let keys = Object.keys(this.getAttributes());
        for (let i = 0; i < keys.length; i++) {
            let attribute: EnterpriseComponentAttribute = this.getAttributes()[keys[i]];

            if (!attribute.getComputed()) {
                let record: Map<string, any> = page![this.getRecordPointer()]
                let originalValue: any = record.get(attribute.getName() + "_original");
                if (originalValue !== undefined && attribute.getName() !== "USID") {
                    if (originalValue === record.get(attribute.getName())) {
                        continue;
                    }
                    let value = record.get(attribute.getName());
                    if (attribute.getType() === "Number") {
                        if (value === "") {
                            out.set(attribute.getName(), null);
                        } else {
                            out.set(attribute.getName(), parseInt(value));
                        }
                    } else if (attribute.getType() === "String") {
                        if (value === "") {
                            out.set(attribute.getName(), null);
                        } else {
                            out.set(attribute.getName(), value);
                        }
                    } else {
                        out.set(attribute.getName(), record.get(attribute.getName()));
                    }
                }
            }
        }
        return Object.fromEntries(out);
    };

    translate_operator(operator: string) {
        if (operator === "Contains") {
            return "CONTAINS";
        }
        if (operator === "Contains Keywords") {
            return "SEARCH";
        }
        if (operator === "Does not contain") {
            return "DOES NOT CONTAIN"
        }
        if (operator === "Is equal to") {
            return "EQUALS"
        }
        if (operator === "Is not equal to") {
            return "NOT EQUAL"
        }
        if (operator === "Starts with") {
            return "STARTS WITH"
        }
        if (operator === "Ends with") {
            return "ENDS WITH"
        }
        if (operator === "Is null") {
            return "IS NULL"
        }
        if (operator === "Is not null") {
            return "IS NOT NULL"
        }
        if (operator === "Is EMPTY") {
            return "IS EMPTY"
        }
        if (operator === "Is in") {
            return "IN"
        }
        if (operator === "Is not in") {
            return "NOT IN"
        }
    }

    prepareQueryPayload() {
        let out: Map<string, any> = new Map<string, any>();

        let page: Map<string, any>[] | undefined = this.getBuffer().get(this.getPagePointer());

        if (page === undefined) {
            return out;
        }

        let keys = Object.keys(this.getAttributes());
        for (let i = 0; i < keys.length; i++) {
            let attribute: EnterpriseComponentAttribute = this.getAttributes()[keys[i]];

            if (!attribute.getComputed()) {
                let record: Map<string, any> = page![this.getRecordPointer()]
                if (record.get(attribute.getName()) !== undefined) {
                    let value = record.get(attribute.getName());
                    if (attribute.getType() === "Number") {
                        if (value === "" || value === null) {
                            // do nothing
                        } else {
                            out.set(attribute.getName(), parseInt(value));
                        }
                    } else if (attribute.getType() === "String") {
                        if (value === "" || value === null) {
                            // do nothing
                        } else {
                            out.set(attribute.getName(), value);
                            if (record.get(attribute.getName() + "_query") !== undefined) {
                                out.set(attribute.getName() + "_query", this.translate_operator(record.get(attribute.getName() + "_query")));
                            }
                        }
                    } else {
                        out.set(attribute.getName(), record.get(attribute.getName()));
                    }
                }
            }
        }
        return out;
    }

    previousRecord = (enable_events = true) => {
        if (this.hasEnterpriseComponentBeenModified()) {
            return false;
        }

        if (!this.isValid()) {
            return false;
        }

        let out: boolean;

        let page: Map<string, any>[] | undefined = this.getBuffer().get(this.getPagePointer());
        if (this.getBuffer().size === 0 || page === undefined) {
            out = false;
        } else {
            if (this.getRecordPointer() && page![this.getRecordPointer()].get("RowSelected") === true) {
                page[this.getRecordPointer()].set("RowSelected", false);
            }

            this.recordPointer -= 1;
            page[this.recordPointer].set("RowSelected", true);
            if (enable_events) this.notifyRecordChangedListeners();
            out = true;
        }

        if (out) {
            this.invalidateChildren();
        }

        return out;
    };

    refreshEnterpriseComponentSuccess(data: any) {
        this.setMode(EnterpriseComponent.MODE_RESULTS);

        let results: any = data["Results"][0];

        let page: Map<string, any>[] | undefined = this.resultsBuffer.get(this.getPagePointer());

        if (this.getBuffer().size === 0 || page === undefined) {
            // no nothing
        } else {
            for (let i = 0; i < page.length; i++) {
                let row: Map<string, any> = page[i];

                if (row.get("USID") === results["USID"]) {
                    let attributes = this.getAttributes();

                    for (const key of Object.keys(attributes)) {
                        row.set(key, results[key]);
                    }
                }
            }
        }

        this.isComponentValid = true;

        this.notifySingleRecordRefreshCompletedListeners();

        this.waiting = false;
    }

    removeDeleteStartedListener(obj: any) {
        let arr = this.deleteStartedListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeDeleteCompletedListener(obj: any) {
        let arr = this.deleteCompletedListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeInsertStartedListener(obj: any) {
        let arr = this.insertStartedListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeInsertCompletedListener(obj: any) {
        let arr = this.insertCompletedListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeInsertRollbackCompletedListener(obj: any) {
        let arr = this.insertRollbackCompletedListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeInvalidListener(obj: any) {
        let arr = this.invalidListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeLoadCompletedListener(obj: any) {
        let arr = this.loadCompletedListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeLoadStartedListener(obj: any){
        let arr = this.loadStartedListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeModeChangedListener(obj: any){
        let arr = this.modeChangedListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeNewQueryRecordListener(obj: any){
        let arr = this.newQueryRecordListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeNewDeleteRecordListener(obj: any){
        let arr = this.newDeleteRecordListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeNewEditRecordListener(obj: any){
        let arr = this.newEditRecordListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeNewRecordListener(obj: any){
        let arr = this.newRecordListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeRecordChangedListener(obj: any){
        let arr = this.recordChangedListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeRecordWillChangeListener(obj: any){
        let arr = this.recordWillChangeListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeSingleRecordRefreshStartedListener(obj: any){
        let arr = this.singleRecordRefreshStartedListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeSingleRecordRefreshCompletedListener(obj: any){
        let arr = this.singleRecordRefreshCompletedListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeUpdateStartedListener(obj: any){
        let arr = this.updateStartedListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeUpdateCompletedListener(obj: any){
        let arr = this.updateCompletedListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeUpdateRollbackCompletedListener(obj: any){
        let arr = this.updateRollbackCompletedListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeQueryRollbackCompletedListener(obj: any){
        let arr = this.queryRollbackCompletedListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeValueChangedListener(obj: any){
        let arr = this.valueChangedListeners;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                arr.splice(i, 1);
                break;
            }
        }

        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === obj) {
                console.error(arr[i]);
            }
        }
    };

    removeAllListeners(obj: any){
        this.removeRecordWillChangeListener(obj);
        this.removeRecordChangedListener(obj);
        this.removeLoadStartedListener(obj);
        this.removeNewDeleteRecordListener(obj);
        this.removeNewEditRecordListener(obj);
        this.removeNewQueryRecordListener(obj);
        this.removeNewRecordListener(obj);
        this.removeLoadCompletedListener(obj);
        this.removeValueChangedListener(obj);
        this.removeInvalidListener(obj);
        this.removeUpdateStartedListener(obj);
        this.removeUpdateCompletedListener(obj);
        this.removeInsertStartedListener(obj);
        this.removeInsertCompletedListener(obj);
        this.removeInsertRollbackCompletedListener(obj);
        this.removeSingleRecordRefreshStartedListener(obj);
        this.removeSingleRecordRefreshCompletedListener(obj);
        this.removeDeleteStartedListener(obj);
        this.removeDeleteCompletedListener(obj);
        this.removeModeChangedListener(obj);
    };

    selectRecord(rowNum: number) {
        if (this.getBuffer().size === 0) return;

        let page: Map<string, any>[] | undefined = this.getBuffer().get(this.getPagePointer())

        if (page === undefined) return;

        if (rowNum === this.getRecordPointer() && page![rowNum].get("RowSelected")) return;

        if (this.getRecordPointer() !== -1) {
            page![this.getRecordPointer()].set("RowSelected", false);
        }

        this.setRecordPointer(rowNum);
        page![rowNum].set("RowSelected", true);
        this.notifyRecordChangedListeners();
        this.invalidateChildren();
    };

    setAttributeValue(attributeName: string, attributeValue: any, force = false, enable_events = true) {
        if (!this.canChangeAttributeValue(attributeName)) {
            return false;
        }

        let page: Map<string, any>[] | undefined = this.getBuffer().get(this.getPagePointer())

        let row: Map<string, any> = page![this.getRecordPointer()];

        let operation: any = row.get("__operation");

        if (operation === "query results" || operation === "update") {
            let previousValue: any = row.get(attributeName);

            if (attributeValue === "NULL") {
                attributeValue = null;
            }
            page![this.getRecordPointer()].set(attributeName, attributeValue);

            row.set("__operation", "update");

            if (force) {
                row.set(attributeName + "_original", previousValue ?? null); // force null because preparePUTPayload() skips undefined
                row.set(attributeName + "_old", previousValue ?? null); // force null because preparePUTPayload() skips undefined
            } else {
                if (row.get(attributeName + "_original") === undefined && row.get(attributeName) !== previousValue) {
                    row.set(attributeName + "_original", previousValue);
                    row.set(attributeName + "_old", previousValue);
                } else {
                    row.set(attributeName + "_old", previousValue);
                }
            }
        } else if ("new" === operation) {
            if (attributeValue === "NULL") {
                attributeValue = null;
            }
            page![this.getRecordPointer()].set(attributeName, attributeValue);
        } else if ("delete" === operation) {
            page![this.getRecordPointer()].set(attributeName, attributeValue);
        } else if ("query" === operation) {
            if (attributeValue === "NULL") {
                attributeValue = null;
            }
            page![this.getRecordPointer()].set(attributeName, attributeValue);
        } else if (undefined === operation) {
            if (attributeValue === "NULL") {
                attributeValue = null;
            }
            page![this.getRecordPointer()].set(attributeName, attributeValue);
        }

        if (enable_events) this.notifyValueChangedListeners();
    };

    setBuffer(buffer: any) {
        this.resultsBuffer = buffer;
    };

    setEnterpriseObject(eo: EnterpriseObject) {
        this.enterpriseObject = eo;
    };

    setIDOCRequest(request: string) {
        this.idocRequest = request;
    };

    setIDOCResponse(response: string) {
        this.idocResponse = response;
    };

    setIsValid(isValid: boolean) {
        this.isComponentValid = isValid;
    };

    setMode(mode: number) {
        this.mode = mode;

        this.notifyModeChangedListeners();
    };

    setPagePointer(page: number) {
        this.pagePointer = page;
    };

    setRecordPointer(position: number) {
        this.recordPointer = position;
    };

    setSearchSpecification(searchSpecification: any) {
        if (searchSpecification === null) {
            this.searchSpecification = {};
        } else {
            this.searchSpecification = searchSpecification;
        }
    };

    setSortSpecification(sortSpecification: SortSpecification[]) {
        this.sortSpecification = sortSpecification;
    }

    setStore(value: any) {
        this.store = value
    };

    setSupportingData(data: any) {
        this.supportingData = data;
    };

    setVisibilityMode(mode: string) {
        this.visibilityMode = mode;
    };

    setVisibilityValue(value: string) {
        this.visibilityValue = value;
    };

    updateEnterpriseComponentFailed() {
        EventBus.error.emit("show-system-error");
    };

    updateEnterpriseComponentSuccess = (data: any) => {
        let dataset = data?.Results ?? data;

        for (let rowIndex = 0; rowIndex < dataset.length; rowIndex++) {
            let updatedRow = [dataset.find((element: any) => element["USID"] === this.getBuffer().get(this.pagePointer)![this.recordPointer].get("USID"))]
            if (updatedRow.length === 1) {
                let keys = Object.keys(updatedRow[0]);
                for (let keysIndex = 0; keysIndex < keys.length; keysIndex++) {
                    let key = keys[keysIndex];

                    let page: Map<string, any>[] = this.getBuffer().get(this.getPagePointer())!;
                    page[this.getRecordPointer()].set(key, updatedRow[0][key]);
                    page[this.getRecordPointer()].set("RowNum", rowIndex);
                    page[this.getRecordPointer()].set("__operation", "query results");
                }
            }
        }

        if (dataset.length > 0) {
            this.getBuffer().get(this.getPagePointer())![this.getRecordPointer()].set("RowSelected", true);
        }

        this.setMode(EnterpriseComponent.MODE_RESULTS);

        this.notifyUpdateCompletedListeners();
    };
}

export default EnterpriseComponent;